import { useState, FC, useCallback } from "react";
import cx from "classnames";
import Tabs from "../tabs";
import styles from "./car-card-grid.module.scss";
import "./car-card-common.scss";
import { TransitStatus, Vehicle, VehicleType } from "../../../types/vehicle";
import {
  ActionButtonData,
  ActionButtonPosition,
  ClientSiteData,
  PropertyVisualStyle,
  ShowIfNotSetValues,
} from "../../../types/site";
import ActionButton, { ActionButtonPrefetchedData } from "../vdp-buttons/actionButton";
import PricePropertyValue from "./pricePropertyValue";
import CarFavoriteButton from "./car-favorite-button";
import { PageContext } from "../../../types/ui";
import ClickToCallButton from "../click-to-call-button";
import { getPhoneForVehicle } from "../../../utils/ui";
import CarCompareCheckbox from "./car-compare-checkbox";
import { getTranslationModule, Translations } from "../../../utils/translations";
import VehiclePropertyView from "./vehicle-property-view";
import CarCardGalery from "./car-card-galery/car-card-galery";
import { CustomTooltipType } from "./customTooltip/customTooltip";
import { CardTab, PriceDisclaimerPosition, VehicleConditionalCardData } from "../../../types/blocks";
import { vehiclePriceFormatter, visualPriceStyleFromPropertyStyle } from "../../../utils/vehicleProperties";
import VehicleTypeView from "../vehicle-type/vehicle-type";
import VehicleYear from "../vehicle-year/vehicle-year";

interface ICarCardGrid {
  className?: string;
  actionButtons: ActionButtonData[];
  vehicle: Vehicle;
  siteData: ClientSiteData;
  pageContext: PageContext;
  vehicleCardData?: VehicleConditionalCardData;
  defaultTab: CardTab;
  translations: Translations;
  onVehicleClick: (vehicle: Vehicle) => void;
  preventShowPrice: boolean;
  showClickToCall: boolean;
  showTabs?: boolean;
}

const CarCardGrid: FC<ICarCardGrid> = ({
  vehicle,
  className,
  actionButtons,
  siteData,
  vehicleCardData,
  pageContext,
  defaultTab,
  translations,
  onVehicleClick,
  preventShowPrice,
  showClickToCall,
  showTabs,
}) => {
  const t_common = getTranslationModule(translations, "common");

  const [, setFinalPrice] = useState<number | undefined>(undefined);
  const actionButtonsWithPrefetchedData: ActionButtonPrefetchedData[] = actionButtons.map((item, index) => ({
    actionButtonData: item,
    prefetchedValue: vehicle.actionButtonsData![index],
  }));
  const underImagesActionButtons = actionButtonsWithPrefetchedData.filter(
    (item) => item.actionButtonData.position === ActionButtonPosition.UNDER_IMAGES,
  );
  const bottomActionButtons = actionButtonsWithPrefetchedData.filter(
    (item) => item.actionButtonData.position === ActionButtonPosition.BOTTOM,
  );

  const tabsInfos = vehicleCardData ? [vehicleCardData.infoTab, vehicleCardData.priceTab] : [];
  const priceStyle = visualPriceStyleFromPropertyStyle(vehicleCardData?.priceStyle);
  const priceDisclaimer = vehicleCardData?.priceDisclaimer;
  const priceDisclaimerPosition = vehicleCardData?.priceDisclaimerPosition || PriceDisclaimerPosition.TOOLTIP;

  const initialTab = vehicleCardData?.defaultTab ? vehicleCardData?.defaultTab : defaultTab;
  const initialTabIndex = initialTab === CardTab.PRICE ? 1 : 0;

  const [currentTab, setCurrentTab] = useState<number>(initialTabIndex);
  const currentTabInfo = tabsInfos?.[currentTab];
  const photos = vehicle?.photosMedium || vehicle.photos;
  const dealerPhoto =
    siteData?.noImagesPhoto === "" || siteData?.noImagesPhoto === undefined
      ? ""
      : siteData?.baseUrl + siteData?.noImagesPhoto;

  const onVehicleDataChanged = () => {
    if (vehicle.finalPrice) {
      setCurrentTab(1);
      setFinalPrice(vehicle.finalPrice);
    }
  };
  const priceTextValue = vehiclePriceFormatter(vehicle, vehicleCardData?.priceField);

  const vehicleClickHandler = useCallback(() => {
    if (onVehicleClick) {
      onVehicleClick(vehicle);
    }
  }, [onVehicleClick, vehicle]);

  const showPrice = !preventShowPrice || !!vehicle.finalPrice;
  const vehicleStatus =
    vehicle.type === VehicleType.NEW
      ? vehicle.transitStatus && vehicle.transitStatus !== TransitStatus.IN_STOCK
        ? t_common(vehicle.transitStatus)
        : t_common(vehicle.type)
      : t_common(vehicle.type);

  const hasDiscount = Boolean(vehicleCardData?.priceTab?.properties?.find((item) => item.name === "discount"));

  return (
    <div
      className={cx(styles.container, styles.grid, className)}
      role="listitem"
      aria-label={`${vehicle.year} ${vehicle.make} ${vehicle.model} ${vehicle.trim} vehicle card`}
    >
      <div className={styles.header}>
        <VehicleTypeView vehicle={vehicle} vehicleStatus={vehicleStatus} />
        <VehicleYear year={vehicle.year} className={styles.year} />
        <CarFavoriteButton vehicle={vehicle} translations={translations} hideTitle />
      </div>
      <CarCardGalery
        photos={photos}
        dealerPhoto={dealerPhoto}
        vehicle={vehicle}
        translations={translations}
        onVehicleDataChanged={onVehicleDataChanged}
        underImagesActionButtons={underImagesActionButtons}
        vehicleClickHandler={vehicleClickHandler}
        carCardType="grid"
        hasDiscount={hasDiscount}
        specialText={vehicleCardData?.specialText}
        specialColor={vehicleCardData?.specialColor}
      />
      <div className={styles.description}>
        <div className={styles.descriptionContainer}>
          <a title={`${vehicle.year} ${vehicle.make} ${vehicle.model} ${vehicle.trim}`} onClick={vehicleClickHandler}>
            <h3 className={styles.name}>{`${vehicle.year} ${vehicle.make}`}</h3>
            <h3 className={styles.make}>{`${vehicle.model} ${vehicle.trim}`}</h3>
          </a>
          {vehicle.stock && (
            <span className={styles.stock}>
              {<b>Stock:</b>} {vehicle.stock}
            </span>
          )}
        </div>
        <PricePropertyValue
          propertyValue={priceTextValue}
          className={cx(styles.price, {
            [styles.priceWithMt]: priceStyle !== PropertyVisualStyle.INVERTED,
          })}
          propertyVisualStyle={priceStyle}
          disclaimer={priceDisclaimerPosition === PriceDisclaimerPosition.TOOLTIP ? priceDisclaimer : ""}
          tooltipType={CustomTooltipType.SRP_GRID_PRICE}
          phone={getPhoneForVehicle(siteData, vehicle)}
          callToPriceClassName={cx(styles.price, styles.priceSmall)}
          translations={translations}
          showIfNotSet={showPrice ? ShowIfNotSetValues.CALL_TO_PRICE : ShowIfNotSetValues.NOTHING}
        />
      </div>
      {showTabs ? (
        vehicleCardData?.infoTab?.properties?.map((item, index) => (
          <VehiclePropertyView
            key={index}
            property={item}
            vehicle={vehicle}
            siteData={siteData}
            showPrice={showPrice}
            translations={translations}
            className={styles.info}
            titleClassName={styles.infoTitle}
            callToPriceClassName={cx(styles.price, styles.priceSmall)}
            valueClassName={styles.infoValue}
            tooltipType={CustomTooltipType.SRP_GRID_PROPERTY}
          />
        ))
      ) : (
        <>
          <Tabs
            className={styles.tabs}
            items={tabsInfos?.map((item) => item.title) || []}
            currentTab={currentTab}
            setCurrentTab={setCurrentTab}
          />
          {/* Info */}
          {currentTab === 0 && (
            <div className={styles.tabContent}>
              {currentTabInfo?.properties &&
                currentTabInfo?.properties?.map((item, index) => (
                  <VehiclePropertyView
                    key={index}
                    property={item}
                    vehicle={vehicle}
                    siteData={siteData}
                    showPrice={showPrice}
                    translations={translations}
                    className={styles.info}
                    titleClassName={styles.infoTitle}
                    callToPriceClassName={cx(styles.price, styles.priceSmall)}
                    valueClassName={styles.infoValue}
                    tooltipType={CustomTooltipType.SRP_GRID_PROPERTY}
                  />
                ))}
            </div>
          )}
          {/* Prices */}
          {currentTab === 1 && (
            <div className={styles.tabContent}>
              <div className={styles.priceTabContent}>
                {currentTabInfo?.properties &&
                  currentTabInfo?.properties?.map((item, index) => (
                    <VehiclePropertyView
                      key={index}
                      property={item}
                      vehicle={vehicle}
                      siteData={siteData}
                      showPrice={showPrice}
                      translations={translations}
                      className={styles.priceInfo}
                      titleClassName={styles.priceInfoTitle}
                      callToPriceClassName={cx(styles.price, styles.priceSmall)}
                      valueClassName={styles.priceInfoValue}
                      tooltipType={CustomTooltipType.SRP_GRID_PROPERTY}
                    />
                  ))}
                {priceDisclaimerPosition === PriceDisclaimerPosition.BOTTOM && !!priceDisclaimer && (
                  <div className={styles.priceDisclaimer}>
                    <div className={styles.separator} />
                    <div className={styles.priceDisclaimerText}>{priceDisclaimer}</div>
                  </div>
                )}
              </div>
              {!!vehicleCardData?.contentBelowPrices && (
                <div
                  className={styles.contentBelowPrices}
                  dangerouslySetInnerHTML={{ __html: vehicleCardData.contentBelowPrices }}
                />
              )}
            </div>
          )}
          {bottomActionButtons?.length > 0 && (
            <div className={styles.buttons}>
              {bottomActionButtons.map((item, index) => (
                <ActionButton
                  key={index}
                  prfetchedData={item.prefetchedValue}
                  className={styles.button}
                  actionButton={item.actionButtonData}
                  currentVehicle={vehicle}
                  vehicleDataChanged={onVehicleDataChanged}
                  layoutType="list"
                />
              ))}
              {showClickToCall && (
                <ClickToCallButton
                  phone={getPhoneForVehicle(siteData, vehicle)}
                  isSmartphone={pageContext.isSmartphone}
                  translations={translations}
                />
              )}
            </div>
          )}
          <div className={styles.details}>
            <CarCompareCheckbox vehicle={vehicle} translations={translations} />
            <a title={t_common("view_details")} className={styles.viewDetails} onClick={vehicleClickHandler}>
              {t_common("view_details")}
            </a>
          </div>
        </>
      )}
    </div>
  );
};

export default CarCardGrid;
